<template>
    <div class="icl-chat" :class="variant === 'modal' ? 'icl-chat-modal' : ''">
        <div ref="messagesContainer" class="icl-chat-messages">
            <div v-if="loading" class="icl-chat-state">
                <font-awesome-icon :icon="['fas', 'spinner']" spin class="icl-chat-state-icon" />
                <p>Loading conversation...</p>
            </div>
            <div v-else-if="messages.length === 0" class="icl-chat-state">
                <div class="icl-chat-state-icon-wrap">
                    <font-awesome-icon :icon="['fas', 'comment-dots']" class="icl-chat-state-icon" />
                </div>
                <p class="icl-chat-state-title">No messages yet</p>
                <p class="icl-chat-state-sub">
                    {{ canReply ? 'Send a reply to start the conversation.' : 'There are no messages to display.' }}
                </p>
            </div>
            <template v-else>
                <div
                    v-for="msg in messages"
                    :key="msg.id"
                    class="icl-chat-row"
                    :class="isOwnMessage(msg) ? 'icl-chat-row-own' : 'icl-chat-row-other'"
                >
                    <div class="icl-chat-bubble" :class="isOwnMessage(msg) ? 'icl-chat-bubble-own' : 'icl-chat-bubble-other'">
                        <div class="icl-chat-bubble-label">{{ messageLabel(msg) }}</div>
                        <div class="icl-chat-bubble-text">{{ msg.message }}</div>
                        <div class="icl-chat-bubble-time">{{ formatDateTime(msg.created_at) }}</div>
                    </div>
                </div>
            </template>
        </div>

        <form v-if="canReply" class="icl-chat-composer" @submit.prevent="sendMessage">
            <div class="icl-chat-composer-inner">
                <textarea
                    v-model="replyText"
                    rows="2"
                    placeholder="Write your reply..."
                    class="icl-chat-input"
                    :disabled="sending"
                ></textarea>
                <button
                    type="submit"
                    class="icl-chat-send"
                    :disabled="sending || !replyText.trim()"
                >
                    <font-awesome-icon v-if="sending" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else :icon="['fas', 'paper-plane']" />
                    <span>{{ sending ? 'Sending' : 'Send' }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const GET_MESSAGES = gql`
  query GetInternshipClinicalPlacementEnrollmentMessages($enrollmentId: ID!) {
    getInternshipClinicalPlacementEnrollmentMessages(enrollment_id: $enrollmentId) {
      error
      message
      data {
        id
        message
        sender_type
        created_at
        user {
          id
          name
        }
      }
    }
  }
`;

const ADD_MESSAGE = gql`
  mutation AddInternshipClinicalPlacementEnrollmentMessage($enrollmentId: ID!, $message: String!) {
    addInternshipClinicalPlacementEnrollmentMessage(enrollment_id: $enrollmentId, message: $message) {
      error
      message
    }
  }
`;

export default {
    name: 'InternshipClinicalPlacementEnrollmentChat',
    props: {
        enrollmentId: {
            type: [Number, String],
            required: true,
        },
        studentName: {
            type: String,
            default: 'Student',
        },
        variant: {
            type: String,
            default: 'default',
        },
        canReply: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            messages: [],
            replyText: '',
            loading: false,
            sending: false,
        };
    },
    computed: {
        currentUserId() {
            const id = this.$store.getters.userId;
            return id ? parseInt(id, 10) : null;
        },
    },
    watch: {
        enrollmentId: {
            immediate: true,
            handler() {
                this.fetchMessages();
            },
        },
        loading(isLoading) {
            if (!isLoading) {
                this.scrollToBottom();
            }
        },
        messages() {
            if (!this.loading) {
                this.scrollToBottom();
            }
        },
    },
    methods: {
        isOwnMessage(msg) {
            return msg && msg.user && this.currentUserId && parseInt(msg.user.id, 10) === this.currentUserId;
        },
        messageLabel(msg) {
            if (this.isOwnMessage(msg)) {
                return 'You';
            }
            if (msg.sender_type === 'student') {
                return this.studentName;
            }
            return msg.user && msg.user.name ? msg.user.name : 'Staff';
        },
        formatDateTime(dateStr) {
            if (!dateStr) return '';
            const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
            return new Date(dateStr).toLocaleString(undefined, options);
        },
        scrollToBottom() {
            var self = this;
            var doScroll = function () {
                var el = self.$refs.messagesContainer;
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            };
            this.$nextTick(function () {
                doScroll();
                // Modal open/transition can delay layout; retry briefly.
                setTimeout(doScroll, 50);
                setTimeout(doScroll, 180);
            });
        },
        async fetchMessages() {
            if (!this.enrollmentId) {
                this.messages = [];
                return;
            }
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_MESSAGES,
                    variables: { enrollmentId: String(this.enrollmentId) },
                    fetchPolicy: 'network-only',
                });
                const res = data.getInternshipClinicalPlacementEnrollmentMessages;
                if (res.error) {
                    if (this.$swal) {
                        this.$swal.fire('Error', res.message, 'error');
                    }
                    this.messages = [];
                    return;
                }
                this.messages = res.data || [];
            } catch (err) {
                console.error('Failed to load enrollment messages:', err);
                this.messages = [];
            } finally {
                this.loading = false;
            }
        },
        async sendMessage() {
            if (!this.canReply) return;
            const text = this.replyText.trim();
            if (!text || this.sending) return;
            this.sending = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: ADD_MESSAGE,
                    variables: {
                        enrollmentId: String(this.enrollmentId),
                        message: text,
                    },
                });
                const res = data.addInternshipClinicalPlacementEnrollmentMessage;
                if (res.error) {
                    if (this.$swal) {
                        this.$swal.fire('Error', res.message, 'error');
                    }
                    return;
                }
                this.replyText = '';
                await this.fetchMessages();
            } catch (err) {
                if (this.$swal) {
                    const msg = err && err.message ? err.message : 'Failed to send message';
                    this.$swal.fire('Error', msg, 'error');
                }
            } finally {
                this.sending = false;
            }
        },
    },
};
</script>

<style scoped>
.icl-chat {
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    overflow: hidden;
    background: #f8fafc;
}

.icl-chat-modal {
    border: none;
    border-radius: 0;
    background: transparent;
}

.icl-chat-messages {
    min-height: 220px;
    max-height: 360px;
    overflow-y: auto;
    padding: 1rem 1.125rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

/* Inside the modal the list has to share the viewport with the header, composer and hint. */
.icl-chat-modal .icl-chat-messages {
    min-height: 180px;
    max-height: 38vh;
}

.icl-chat-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem 1rem;
    color: #64748b;
    font-size: 0.875rem;
}

.icl-chat-state-icon-wrap {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: #e0e7ff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;
}

.icl-chat-state-icon {
    font-size: 1.25rem;
    color: #4f46e5;
}

.icl-chat-state-title {
    margin: 0 0 0.25rem 0;
    font-weight: 600;
    color: #334155;
    font-size: 0.9375rem;
}

.icl-chat-state-sub {
    margin: 0;
    font-size: 0.8125rem;
    color: #94a3b8;
}

.icl-chat-row {
    display: flex;
    margin-bottom: 0.75rem;
}

.icl-chat-row-own {
    justify-content: flex-end;
}

.icl-chat-row-other {
    justify-content: flex-start;
}

.icl-chat-bubble {
    max-width: 85%;
    padding: 0.625rem 0.875rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    line-height: 1.45;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.icl-chat-bubble-own {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    color: #fff;
    border-bottom-right-radius: 0.25rem;
}

.icl-chat-bubble-other {
    background: #fff;
    color: #1e293b;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #6366f1;
    border-bottom-left-radius: 0.25rem;
}

.icl-chat-bubble-label {
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.25rem;
    opacity: 0.85;
}

.icl-chat-bubble-other .icl-chat-bubble-label {
    color: #6366f1;
    opacity: 1;
}

.icl-chat-bubble-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.icl-chat-bubble-time {
    font-size: 0.6875rem;
    margin-top: 0.375rem;
    opacity: 0.75;
}

.icl-chat-composer {
    border-top: 1px solid #e2e8f0;
    background: #fff;
    padding: 0.875rem 1rem;
}

.icl-chat-composer-inner {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

@media (min-width: 480px) {
    .icl-chat-composer-inner {
        flex-direction: row;
        align-items: flex-end;
    }
}

.icl-chat-input {
    flex: 1;
    width: 100%;
    min-height: 2.75rem;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    color: #1e293b;
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 0.75rem;
    resize: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.icl-chat-input:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
    background: #fff;
}

.icl-chat-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.icl-chat-send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.75rem;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    flex-shrink: 0;
}

.icl-chat-send:not(:disabled):hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
    transform: translateY(-1px);
}

.icl-chat-send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
}
</style>
